import * as THREE from 'three/webgpu';

const JOINT_DEFINITIONS = [
  { axis: [0, 0, -1], limits: [-170, 170] },
  { axis: [0.995563, 0.094093, 0], limits: [-100, 130] },
  { axis: [0.995563, 0.094093, 0], limits: [-150, 150] },
  { axis: [-0.083464, 0.883093, -0.461715], limits: [-175, 175] },
  { axis: [0.995563, 0.094093, 0], limits: [-120, 120] },
  { axis: [0, 0, -1], limits: [-175, 175] },
].map((definition, index) => ({
  ...definition,
  name: `ABB_Joint_${index + 1}`,
  axis: new THREE.Vector3(...definition.axis).normalize(),
  min: THREE.MathUtils.degToRad(definition.limits[0]),
  max: THREE.MathUtils.degToRad(definition.limits[1]),
}));

// The bare flange points its local +Z out of the tool face. Keeping that axis
// world-down lets a future gripper stay level while its tube remains upright.
const TOOL_Z_DOWN = new THREE.Vector3(0, -1, 0);

function createTargetMarker(scale) {
  const marker = new THREE.Group();
  marker.name = 'ABB_IK_Target';
  const material = new THREE.MeshBasicMaterial({
    color: 0x56d8ff,
    transparent: true,
    opacity: 0.8,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  marker.add(new THREE.Mesh(new THREE.SphereGeometry(scale * 0.28, 16, 10), material));
  for (let index = 0; index < 3; index += 1) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(scale, scale * 0.065, 8, 36),
      material,
    );
    if (index === 1) ring.rotation.x = Math.PI / 2;
    if (index === 2) ring.rotation.y = Math.PI / 2;
    marker.add(ring);
  }
  return marker;
}

export class RobotArmIK {
  constructor(gltfScene) {
    this.root = gltfScene.getObjectByName('ABB_Rig');
    if (!this.root) throw new Error('abb_ik.glb is missing ABB_Rig.');
    this.root.removeFromParent();
    this.root.name = 'ABB_CRB15000_Authored_IK_Rig';
    this.root.position.set(0, 0, 0);
    this.root.rotation.set(Math.PI / 2, 0, 0);
    this.root.visible = false;
    this.autonomousPosition = this.root.position.clone();
    this.autonomousQuaternion = this.root.quaternion.clone();
    this.externalPosition = this.autonomousPosition.clone();
    this.externalQuaternion = this.autonomousQuaternion.clone();

    this.meshes = [];
    this.root.traverse((object) => {
      if (object.isMesh) {
        object.frustumCulled = false;
        this.meshes.push(object);
      }
    });
    this.linkMeshes = this.meshes.filter(
      (mesh) => mesh.name.includes('ABB_Joint_')
        && mesh.name.endsWith('_Mesh')
        && !mesh.name.includes('ABB_Joint_1_Mesh'),
    );

    this.joints = JOINT_DEFINITIONS.map((definition) => {
      const pivot = this.root.getObjectByName(definition.name);
      if (!pivot) throw new Error(`abb_ik.glb is missing ${definition.name}.`);
      return { ...definition, pivot, restQuaternion: pivot.quaternion.clone(), angle: 0 };
    });
    this.effector = this.root.getObjectByName('ABB_TCP');
    if (!this.effector) throw new Error('abb_ik.glb is missing ABB_TCP.');

    this.root.updateMatrixWorld(true);
    const modelBounds = new THREE.Box3().setFromObject(this.root, true);
    const markerScale = Math.max(modelBounds.getSize(new THREE.Vector3()).length() * 0.022, 0.012);
    this.restTarget = this.root.worldToLocal(this.effector.getWorldPosition(new THREE.Vector3()));
    this.target = createTargetMarker(markerScale);
    this.target.position.copy(this.restTarget);
    this.root.add(this.target);

    this.active = true;
    this.controlMode = 'autonomous';
    this.elapsed = 0;
    this.floorY = -Infinity;
    this.floorClearance = 0.025;
    this.preferredJoint4Angle = null;
    this._jointWorld = new THREE.Vector3();
    this._effectorWorld = new THREE.Vector3();
    this._targetWorld = new THREE.Vector3();
    this._axisWorld = new THREE.Vector3();
    this._from = new THREE.Vector3();
    this._to = new THREE.Vector3();
    this._cross = new THREE.Vector3();
    this._toolAxisWorld = new THREE.Vector3();
    this._toolAxisFrom = new THREE.Vector3();
    this._toolAxisTo = new THREE.Vector3();
    this._parentQuaternion = new THREE.Quaternion();
    this._rotation = new THREE.Quaternion();
  }

  setVisible(visible) {
    this.root.visible = visible;
    this.target.visible = visible && this.active && this.controlMode === 'autonomous';
    if (!visible) this.reset();
  }

  setControlMode(mode) {
    if (mode !== 'autonomous' && mode !== 'external') {
      throw new Error(`Unsupported robot arm control mode: ${mode}`);
    }
    if (this.controlMode !== mode) {
      this.root.position.copy(mode === 'external' ? this.externalPosition : this.autonomousPosition);
      this.root.quaternion.copy(mode === 'external' ? this.externalQuaternion : this.autonomousQuaternion);
      this.root.updateMatrixWorld(true);
      this.reset();
    }
    this.controlMode = mode;
    this.target.visible = this.root.visible && this.active && mode === 'autonomous';
  }

  setActive(active) {
    this.active = active;
    this.target.visible = this.root.visible && active && this.controlMode === 'autonomous';
    if (active) this.elapsed = 0;
  }

  getRestTargetWorld(target = new THREE.Vector3()) {
    this.root.updateMatrixWorld(true);
    return target.copy(this.restTarget).applyMatrix4(this.root.matrixWorld);
  }

  getExternalRestTargetWorld(target = new THREE.Vector3()) {
    this.root.updateMatrixWorld(true);
    const position = this.root.position.clone();
    const quaternion = this.root.quaternion.clone();
    this.root.position.copy(this.externalPosition);
    this.root.quaternion.copy(this.externalQuaternion);
    this.root.updateMatrixWorld(true);
    target.copy(this.restTarget).applyMatrix4(this.root.matrixWorld);
    this.root.position.copy(position);
    this.root.quaternion.copy(quaternion);
    this.root.updateMatrixWorld(true);
    return target;
  }

  getExternalBaseWorld(target = new THREE.Vector3()) {
    const position = this.root.position.clone();
    const quaternion = this.root.quaternion.clone();
    this.root.position.copy(this.externalPosition);
    this.root.quaternion.copy(this.externalQuaternion);
    this.root.updateMatrixWorld(true);
    target.set(0, 0, 0).applyMatrix4(this.root.matrixWorld);
    this.root.position.copy(position);
    this.root.quaternion.copy(quaternion);
    this.root.updateMatrixWorld(true);
    return target;
  }

  configureExternalAlignment(targetBounds) {
    this.floorY = targetBounds.min.y;
    const modelHeight = targetBounds.getSize(new THREE.Vector3()).y;
    this.floorClearance = Math.max(modelHeight * 0.012, 0.025);
    const targetCenter = targetBounds.getCenter(new THREE.Vector3());
    this.root.position.set(0, 0, 0);
    this.root.quaternion.copy(this.autonomousQuaternion);
    this.root.updateMatrixWorld(true);
    const currentBounds = new THREE.Box3().setFromObject(this.root, true);
    const currentCenter = currentBounds.getCenter(new THREE.Vector3());
    this.root.position.set(
      targetCenter.x - currentCenter.x,
      targetBounds.min.y - currentBounds.min.y,
      targetCenter.z - currentCenter.z,
    );
    this.root.updateMatrixWorld(true);
    this.externalPosition.copy(this.root.position);
    this.externalQuaternion.copy(this.root.quaternion);
    this.root.position.copy(this.autonomousPosition);
    this.root.quaternion.copy(this.autonomousQuaternion);
    this.root.updateMatrixWorld(true);
  }

  setWorldTarget(worldPosition, joint6Angle = 0, joint4Angle = null) {
    if (!this.root.visible || this.controlMode !== 'external') return;
    this.preferredJoint4Angle = joint4Angle === null
      ? null
      : THREE.MathUtils.clamp(joint4Angle, this.joints[3].min, this.joints[3].max);
    const safeWorldPosition = worldPosition.clone();
    safeWorldPosition.y = Math.max(safeWorldPosition.y, this.floorY + this.floorClearance);
    const joint6 = this.joints[5];
    joint6.angle = THREE.MathUtils.clamp(joint6Angle, joint6.min, joint6.max);
    this.applyJointRotation(joint6);
    // A position-only IK target can choose an elbow-down branch that puts a
    // moving link on the floor. Re-solve once from a raised target when that
    // happens; the TCP remains level while the arm clears the work surface.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      this.target.position.copy(safeWorldPosition);
      this.root.worldToLocal(this.target.position);
      this.solve();
      const lowestLink = this.getLowestMovingLinkY();
      if (lowestLink >= this.floorY + this.floorClearance || attempt === 1) break;
      safeWorldPosition.y += this.floorY + this.floorClearance - lowestLink;
    }
  }

  getLowestMovingLinkY() {
    if (!this.linkMeshes.length) return Infinity;
    this.root.updateMatrixWorld(true);
    const bounds = new THREE.Box3();
    this.linkMeshes.forEach((mesh) => bounds.expandByObject(mesh, true));
    return bounds.min.y;
  }

  applyJointRotation(joint) {
    this._rotation.setFromAxisAngle(joint.axis, joint.angle);
    joint.pivot.quaternion.copy(joint.restQuaternion).multiply(this._rotation);
  }

  reset() {
    this.elapsed = 0;
    for (const joint of this.joints) {
      joint.angle = 0;
      joint.pivot.quaternion.copy(joint.restQuaternion);
    }
    this.target.position.copy(this.restTarget);
  }

  solve() {
    // Solve position and flange direction together. Position-only CCD can reach
    // the same point with the bare flange inverted, which would tip an attached tube.
    for (let iteration = 0; iteration < 16; iteration += 1) {
      for (let index = this.joints.length - 2; index >= 0; index -= 1) {
        const joint = this.joints[index];
        this.root.updateMatrixWorld(true);
        joint.pivot.getWorldPosition(this._jointWorld);
        this.effector.getWorldPosition(this._effectorWorld);
        this.target.getWorldPosition(this._targetWorld);
        joint.pivot.parent.getWorldQuaternion(this._parentQuaternion);
        this._axisWorld.copy(joint.axis).applyQuaternion(this._parentQuaternion).normalize();

        this._from.copy(this._effectorWorld).sub(this._jointWorld);
        this._to.copy(this._targetWorld).sub(this._jointWorld);
        this._from.addScaledVector(this._axisWorld, -this._from.dot(this._axisWorld));
        this._to.addScaledVector(this._axisWorld, -this._to.dot(this._axisWorld));
        if (this._from.lengthSq() < 1e-9 || this._to.lengthSq() < 1e-9) continue;
        this._from.normalize();
        this._to.normalize();

        const sine = this._axisWorld.dot(this._cross.crossVectors(this._from, this._to));
        const cosine = THREE.MathUtils.clamp(this._from.dot(this._to), -1, 1);
        const correction = THREE.MathUtils.clamp(Math.atan2(sine, cosine), -0.11, 0.11);
        joint.angle = THREE.MathUtils.clamp(joint.angle + correction, joint.min, joint.max);
        this.applyJointRotation(joint);
      }

      this.alignToolAxis();
      if (this.preferredJoint4Angle !== null) {
        const wristAxis = this.joints[3];
        const wristCorrection = THREE.MathUtils.clamp(
          this.preferredJoint4Angle - wristAxis.angle,
          -0.06,
          0.06,
        );
        wristAxis.angle = THREE.MathUtils.clamp(
          wristAxis.angle + wristCorrection,
          wristAxis.min,
          wristAxis.max,
        );
        this.applyJointRotation(wristAxis);
      }

      this.root.updateMatrixWorld(true);
      this.effector.getWorldPosition(this._effectorWorld);
      this.target.getWorldPosition(this._targetWorld);
      this._toolAxisWorld.set(0, 0, 1).transformDirection(this.effector.matrixWorld).normalize();
      const positionError = this._effectorWorld.distanceToSquared(this._targetWorld);
      const axisError = 1 - this._toolAxisWorld.dot(TOOL_Z_DOWN);
      if (positionError < 0.000001 && axisError < 0.0005) break;
    }
  }

  alignToolAxis() {
    // Align only the two tilt degrees of freedom. Joint 6 remains a roll around
    // the down-facing TCP Z axis, so it cannot tilt a level gripper.
    for (let index = this.joints.length - 2; index >= 0; index -= 1) {
      const joint = this.joints[index];
      this.root.updateMatrixWorld(true);
      joint.pivot.getWorldQuaternion(this._parentQuaternion);
      this._axisWorld.copy(joint.axis).applyQuaternion(this._parentQuaternion).normalize();
      this._toolAxisWorld.set(0, 0, 1).transformDirection(this.effector.matrixWorld).normalize();

      this._toolAxisFrom.copy(this._toolAxisWorld)
        .addScaledVector(this._axisWorld, -this._toolAxisWorld.dot(this._axisWorld));
      this._toolAxisTo.copy(TOOL_Z_DOWN)
        .addScaledVector(this._axisWorld, -TOOL_Z_DOWN.dot(this._axisWorld));
      if (this._toolAxisFrom.lengthSq() < 1e-9 || this._toolAxisTo.lengthSq() < 1e-9) continue;
      this._toolAxisFrom.normalize();
      this._toolAxisTo.normalize();

      const sine = this._axisWorld.dot(this._cross.crossVectors(this._toolAxisFrom, this._toolAxisTo));
      const cosine = THREE.MathUtils.clamp(this._toolAxisFrom.dot(this._toolAxisTo), -1, 1);
      const correction = THREE.MathUtils.clamp(Math.atan2(sine, cosine) * 0.82, -0.14, 0.14);
      joint.angle = THREE.MathUtils.clamp(joint.angle + correction, joint.min, joint.max);
      this.applyJointRotation(joint);
    }
  }

  update(time, delta) {
    if (!this.root.visible || !this.active || this.controlMode !== 'autonomous') return;
    this.elapsed += delta;
    const transition = THREE.MathUtils.smoothstep(this.elapsed, 0, 1.6);
    const path = this.restTarget.clone().add(new THREE.Vector3(
      Math.cos(time * 0.53) * 0.1,
      Math.sin(time * 0.67) * 0.085,
      Math.sin(time * 0.47) * 0.075,
    ));
    this.target.position.lerpVectors(this.restTarget, path, transition);

    const joint6 = this.joints[5];
    joint6.angle = Math.sin(time * 0.86) * 0.75 * transition;
    this.applyJointRotation(joint6);
    this.solve();
  }
}
